import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import MarketingNav from './MarketingNav';
import Footer from './Footer';
import useIsMobile from '../../lib/useIsMobile';
import useSEO from '../../lib/useSEO';

const C = {
  bg: '#FAFBFC',
  white: '#FFFFFF',
  navy: '#1B2B4B',
  accent: '#2563EB',
  green: '#16A34A',
  muted: '#64748B',
  light: '#94A3B8',
  border: '#E2E8F0',
};

const TAX_RATES = [22, 24, 32, 35, 37];
const STUDY_FEE = 799;
const currentYear = new Date().getFullYear();

function getBonusPercent(year) {
  if (year <= 2022) return 100;
  if (year === 2023) return 80;
  if (year === 2024) return 60;
  if (year === 2025) return 40;
  if (year === 2026) return 20;
  return 0;
}

function formatCurrency(num) {
  if (!num || isNaN(num)) return '$0';
  return '$' + Math.round(num).toLocaleString('en-US');
}

function parsePriceInput(val) {
  return Number(val.replace(/[^0-9]/g, '')) || 0;
}

const years = [];
for (let y = currentYear; y >= 2000; y--) {
  years.push(y);
}

export default function CalculatorPage() {
  const isMobile = useIsMobile();
  useSEO({ title: 'Cost Segregation Savings Calculator | ClearSeg', description: 'Calculate your estimated tax savings from a cost segregation study on your rental property. Free interactive calculator.', canonical: 'https://clearseg.com/calculator' });
  const [priceInput, setPriceInput] = useState('');
  const [propertyType, setPropertyType] = useState('single-family');
  const [yearPurchased, setYearPurchased] = useState(currentYear);
  const [taxRate, setTaxRate] = useState(37);
  const [holdYears, setHoldYears] = useState(10);

  const purchasePrice = parsePriceInput(priceInput);
  const bonusPercent = getBonusPercent(yearPurchased);

  const results = useMemo(() => {
    if (!purchasePrice) return null;
    const reclassifiedLow = purchasePrice * 0.20;
    const reclassifiedHigh = purchasePrice * 0.30;
    const reclassifiedMid = purchasePrice * 0.25;
    const savings = reclassifiedMid * (bonusPercent / 100) * (taxRate / 100);
    const roi = savings > 0 ? (savings / STUDY_FEE) : 0;

    // Recapture modeling
    // Section 1250: accelerated depreciation recaptured at 25%
    const recaptureRate = 0.25;
    const acceleratedDepr = reclassifiedMid * (bonusPercent / 100);
    const recaptureTax = acceleratedDepr * recaptureRate;
    // Time value: discount recapture at 5% annually
    const discountRate = 0.05;
    const pvRecapture = recaptureTax / Math.pow(1 + discountRate, holdYears);
    const netBenefit = savings - pvRecapture;

    return { reclassifiedLow, reclassifiedHigh, reclassifiedMid, savings, roi, recaptureTax, pvRecapture, netBenefit };
  }, [purchasePrice, bonusPercent, taxRate, holdYears]);

  const handlePriceChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    if (raw === '') {
      setPriceInput('');
      return;
    }
    setPriceInput(Number(raw).toLocaleString('en-US'));
  };

  const maxW = { maxWidth: 1200, margin: '0 auto' };
  const sectionPad = { padding: isMobile ? '60px 20px' : '100px 24px' };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    background: C.white,
    border: `1.5px solid ${C.border}`,
    borderRadius: 10,
    color: C.navy,
    fontSize: 16,
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: C.muted,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  };

  const selectStyle = {
    ...inputStyle,
    appearance: 'none',
    WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' fill='%2364748B'%3E%3Cpath d='M1 1l5 5 5-5'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 16px center',
    paddingRight: 40,
    cursor: 'pointer',
  };

  return (
    <div style={{ background: C.bg, color: C.navy, minHeight: '100vh', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}>
      <MarketingNav />

      <section style={{
        ...sectionPad,
        paddingTop: isMobile ? 60 : 100,
        paddingBottom: isMobile ? 40 : 60,
        textAlign: 'center',
        background: C.white,
      }}>
        <div style={{ ...maxW }}>
          <h1 style={{
            fontSize: isMobile ? 28 : 48, fontWeight: 900, lineHeight: 1.1,
            margin: 0, marginBottom: isMobile ? 12 : 20, letterSpacing: '-0.02em',
          }}>
            Cost Segregation Savings Calculator
          </h1>
          <p style={{
            fontSize: isMobile ? 15 : 19, color: C.muted, lineHeight: 1.6,
            margin: 0, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto',
          }}>
            Enter your property details. See your estimated Year-1 tax savings instantly.
          </p>
        </div>
      </section>

      <section style={{ ...sectionPad, paddingTop: isMobile ? 32 : 48 }}>
        <div style={{
          ...maxW,
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? 24 : 36,
          alignItems: 'start',
        }}>
          {/* LEFT: Input Card */}
          <div style={{
            background: C.white, border: `1px solid ${C.border}`,
            borderRadius: 16, padding: isMobile ? '28px 24px' : '40px 36px',
          }}>
            <h2 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, margin: 0, marginBottom: 28 }}>
              Property Details
            </h2>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Purchase Price</label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                  color: C.muted, fontSize: 16, fontWeight: 600, pointerEvents: 'none',
                }}>$</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={priceInput}
                  onChange={handlePriceChange}
                  placeholder="350,000"
                  style={{ ...inputStyle, paddingLeft: 32 }}
                  onFocus={(e) => e.target.style.borderColor = C.accent}
                  onBlur={(e) => e.target.style.borderColor = C.border}
                />
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Property Type</label>
              <select
                value={propertyType}
                onChange={(e) => setPropertyType(e.target.value)}
                style={selectStyle}
              >
                <option value="single-family">Single-Family Residence</option>
              </select>
            </div>

            <div style={{ marginBottom: 28 }}>
              <label style={labelStyle}>Year Purchased</label>
              <select
                value={yearPurchased}
                onChange={(e) => setYearPurchased(Number(e.target.value))}
                style={selectStyle}
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 28 }}>
              <label style={labelStyle}>
                Federal Tax Bracket: <span style={{ color: C.navy }}>{taxRate}%</span>
              </label>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                {TAX_RATES.map((rate) => (
                  <button
                    key={rate}
                    onClick={() => setTaxRate(rate)}
                    style={{
                      flex: 1,
                      padding: '10px 0',
                      borderRadius: 8,
                      border: `1.5px solid ${taxRate === rate ? C.accent : C.border}`,
                      background: taxRate === rate ? `${C.accent}11` : 'transparent',
                      color: taxRate === rate ? C.navy : C.muted,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontFamily: 'inherit',
                    }}
                  >
                    {rate}%
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label style={labelStyle}>
                Planned Hold Period: <span style={{ color: C.navy }}>{holdYears} years</span>
              </label>
              <input
                type="range"
                min={1}
                max={30}
                value={holdYears}
                onChange={(e) => setHoldYears(Number(e.target.value))}
                style={{ width: '100%', accentColor: C.accent, cursor: 'pointer' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.light, marginTop: 4 }}>
                <span>1 yr</span>
                <span>30 yrs</span>
              </div>
            </div>
          </div>

          {/* RIGHT: Results Card */}
          <div style={{
            background: C.white, border: `1px solid ${C.border}`,
            borderRadius: 16, padding: isMobile ? '28px 24px' : '40px 36px',
            position: isMobile ? 'static' : 'sticky',
            top: 100,
          }}>
            <h2 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, margin: 0, marginBottom: 28 }}>
              Estimated Savings
            </h2>

            {!results ? (
              <div style={{
                textAlign: 'center', padding: isMobile ? '40px 16px' : '60px 24px',
                color: C.muted, fontSize: 15,
              }}>
                <div style={{
                  width: 64, height: 64, borderRadius: 16, margin: '0 auto 20px',
                  background: `${C.accent}11`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 28, color: C.accent,
                }}>
                  $
                </div>
                Enter your purchase price to see estimated savings.
              </div>
            ) : (
              <>
                <div style={{
                  background: C.bg, borderRadius: 14, padding: isMobile ? '20px 18px' : '24px 24px',
                  marginBottom: 16, border: `1px solid ${C.border}`,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                    Estimated Reclassifiable Amount
                  </div>
                  <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: C.navy, letterSpacing: '-0.02em' }}>
                    {formatCurrency(results.reclassifiedLow)} &ndash; {formatCurrency(results.reclassifiedHigh)}
                  </div>
                  <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
                    20% &ndash; 30% of purchase price
                  </div>
                </div>

                <div style={{
                  background: '#F0FDF4',
                  borderRadius: 14, padding: isMobile ? '24px 18px' : '28px 24px',
                  marginBottom: 16, border: `1px solid #BBF7D0`,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                    Estimated Year-1 Tax Savings
                  </div>
                  <div style={{
                    fontSize: isMobile ? 32 : 44, fontWeight: 900, letterSpacing: '-0.03em',
                    color: C.green, lineHeight: 1.1,
                  }}>
                    {formatCurrency(results.savings)}
                  </div>
                  <div style={{ fontSize: 13, color: C.muted, marginTop: 8 }}>
                    Based on {taxRate}% tax bracket with {bonusPercent}% bonus depreciation
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
                  <div style={{
                    flex: 1, background: C.bg, borderRadius: 12, padding: '18px 16px',
                    border: `1px solid ${C.border}`, textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                      Study Fee
                    </div>
                    <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: C.navy }}>
                      ${STUDY_FEE.toLocaleString('en-US')}
                    </div>
                  </div>
                  <div style={{
                    flex: 1, background: C.bg, borderRadius: 12, padding: '18px 16px',
                    border: `1px solid ${C.border}`, textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                      ROI
                    </div>
                    <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: results.roi >= 1 ? C.green : C.navy }}>
                      {results.roi >= 0.1 ? results.roi.toFixed(1) : '0'}:1
                    </div>
                  </div>
                </div>

                {bonusPercent === 0 && (
                  <div style={{
                    background: `${C.accent}08`, border: `1px solid ${C.accent}22`,
                    borderRadius: 10, padding: '14px 16px', marginBottom: 20,
                    fontSize: 13, color: C.muted, lineHeight: 1.6,
                  }}>
                    Properties purchased in {yearPurchased} are not eligible for bonus depreciation. Savings will be spread across the accelerated recovery periods (5, 7, and 15 years) instead of being taken in Year 1. A full study will show your multi-year benefit.
                  </div>
                )}

                {/* Recapture Modeling */}
                <div style={{
                  background: C.bg, borderRadius: 14, padding: isMobile ? '20px 18px' : '24px 24px',
                  marginBottom: 24, border: `1px solid ${C.border}`,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>
                    Depreciation Recapture at Sale ({holdYears} yr hold)
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                    <span style={{ fontSize: 14, color: C.muted }}>Recapture tax at sale (25%)</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{formatCurrency(results.recaptureTax)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                    <span style={{ fontSize: 14, color: C.muted }}>Present value of recapture</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{formatCurrency(results.pvRecapture)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>Net benefit after recapture</span>
                    <span style={{ fontSize: 18, fontWeight: 800, color: results.netBenefit > 0 ? C.green : C.navy }}>{formatCurrency(results.netBenefit)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: C.light, marginTop: 8 }}>
                    Recapture discounted at 5% annually. You get the savings now — recapture is paid later in cheaper dollars.
                  </div>
                </div>

                <Link
                  to="/intake"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '100%', padding: '16px 24px',
                    background: C.navy, color: '#FFFFFF',
                    fontSize: 16, fontWeight: 700, borderRadius: 10,
                    textDecoration: 'none', boxSizing: 'border-box',
                    transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                >
                  Start Your Study
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      <section style={{ padding: isMobile ? '0 20px 40px' : '0 24px 60px' }}>
        <div style={{
          ...maxW,
          background: C.white, border: `1px solid ${C.border}`,
          borderRadius: 14, padding: isMobile ? '20px 20px' : '24px 32px',
        }}>
          <p style={{
            fontSize: 13, color: C.muted, lineHeight: 1.8, margin: 0, textAlign: 'center',
          }}>
            Estimates only. Actual results depend on your property's location, age, condition, and improvements. A ClearSeg study provides exact figures certified by a licensed Professional Engineer.
          </p>
        </div>
      </section>

      <section style={{
        padding: isMobile ? '60px 20px' : '80px 24px',
        background: C.navy,
        textAlign: 'center',
      }}>
        <div style={maxW}>
          <h2 style={{
            fontSize: isMobile ? 24 : 36, fontWeight: 800, color: '#FFFFFF',
            margin: 0, marginBottom: 14, lineHeight: 1.2,
          }}>
            Ready to Get Your Exact Savings?
          </h2>
          <p style={{
            fontSize: isMobile ? 15 : 18, color: '#94A3B8',
            margin: 0, marginBottom: isMobile ? 28 : 36, lineHeight: 1.6,
          }}>
            A PE-certified ClearSeg study gives you exact, IRS-compliant figures for your tax return. $799.
          </p>
          <Link
            to="/intake"
            style={{
              display: 'inline-flex', alignItems: 'center',
              padding: isMobile ? '16px 32px' : '18px 44px',
              background: '#FFFFFF', color: C.navy,
              fontSize: isMobile ? 16 : 18, fontWeight: 700,
              borderRadius: 10, textDecoration: 'none',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.92'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            Start Your Study
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
